import React, { useId } from "react";

/**
 * TreadMargin monogram — v4.
 *
 * Distinctive elements:
 *   – A stacked charcoal serif "T" with wide, weighted crossbar and slab foot.
 *   – A bold gold three-peak chevron (reads as an M / a mountain / a margin
 *     arrow) rising THROUGH the T crossbar; the centre peak breaks above the
 *     bar and terminates in a sharp arrowhead — the "growth" moment.
 *   – A gold tread arc at the base that curls under the T, hinting at motion
 *     and tire circumference without being literal.
 *   – Hover-only micro-lift (translateY -1px) — implemented in CSS to avoid
 *     Motion library and keep the icon reusable.
 */
export function LogoMark({ size = 32, className = "" }) {
  const uid = useId().replace(/:/g, "");
  const goldId = `tm_gold_${uid}`;
  const inkId = `tm_ink_${uid}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`tm-logo-mark ${className}`}
      aria-hidden
      data-testid="logo-mark"
    >
      <defs>
        <linearGradient id={goldId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#F0C979" />
          <stop offset="0.55" stopColor="#C9943D" />
          <stop offset="1" stopColor="#8A6318" />
        </linearGradient>
        <linearGradient id={inkId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1A1D23" />
          <stop offset="1" stopColor="#0F1115" />
        </linearGradient>
      </defs>

      {/* Charcoal serif T — wide crossbar with slab endcaps + weighted stem */}
      <path
        d="M8 12 H56 V22 H44 V52 H36 L34 48 L30 48 L28 52 H20 V22 H8 Z"
        fill={`url(#${inkId})`}
      />
      {/* Slab feet on the T stem give it a serif weight */}
      <rect x="18" y="48" width="14" height="6" rx="1.2" fill={`url(#${inkId})`} />
      <rect x="34" y="48" width="14" height="6" rx="1.2" fill={`url(#${inkId})`} />

      {/* Gold three-peak chevron piercing the crossbar — the M / margin arrow */}
      <path
        d="M14 34
           L22 22
           L29 32
           L36 22
           L43 32
           L50 22
           L50 32
           L43 42
           L36 32
           L29 42
           L22 32
           L14 42 Z"
        fill={`url(#${goldId})`}
      />
      {/* Sharp central arrowhead breaking above the crossbar */}
      <path d="M32 6 L42 18 L22 18 Z" fill={`url(#${goldId})`} />
      <rect x="30" y="14" width="4" height="10" rx="1" fill={`url(#${goldId})`} />

      {/* Tread arc curling under the T */}
      <path
        d="M12 58 Q32 66 52 58"
        stroke={`url(#${goldId})`}
        strokeWidth="2.4"
        strokeLinecap="round"
        fill="none"
        opacity="0.9"
      />
      <path
        d="M18 60 Q32 66 46 60"
        stroke="#0F1115"
        strokeWidth="1.4"
        strokeLinecap="round"
        fill="none"
        opacity="0.55"
      />
    </svg>
  );
}

export function LogoLockup({ size = 36 }) {
  return (
    <div className="inline-flex items-center gap-2.5 group" data-testid="logo-lockup">
      <span className="inline-block transition-transform duration-300 group-hover:-translate-y-0.5">
        <LogoMark size={size} />
      </span>
      <span
        className="text-[19px] sm:text-[21px] font-bold tracking-tight leading-none"
        style={{ fontFamily: "Manrope, Inter, sans-serif", letterSpacing: "-0.03em" }}
      >
        <span style={{ color: "#0F1115" }}>Tread</span>
        <span style={{
          backgroundImage: "linear-gradient(180deg,#F0C979 0%,#C9943D 55%,#8A6318 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
        }}>Margin</span>
      </span>
    </div>
  );
}
